import React, { useCallback, useState } from "react";
import {
  makeStyles,
  tokens,
  Card,
  Text,
  Table,
  TableBody,
  TableCell,
  TableCellLayout,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Badge,
  mergeClasses,
  Caption1,
  MessageBar,
  MessageBarBody,
} from "@fluentui/react-components";
import { Warning20Regular } from "@fluentui/react-icons";
import {
  OrderData,
  SKUItem,
  CFROIResponse,
  PaymentEntry,
  ApprovalDetails,
  CommonOrderDetails,
} from "../types/api";
import {
  mapOrderDataToCommon,
  mapApprovalDetailsToCommon,
  createCFROIResponseFromApproval,
  hasSkuPaymentSeries,
  getEnhancedSkuData,
} from "../utils/orderDataMappers";
import SkuChartsModal from "./SkuChartsModal";
import PortfolioCashflowChart from "./PortfolioCashflowChart";

const useStyles = makeStyles({
  orderInfoSection: {
    padding: tokens.spacingVerticalL,
  },
  infoGrid: {
    display: "grid",
    gridTemplateColumns: "200px 1fr",
    gap: tokens.spacingVerticalM,
    marginBottom: tokens.spacingVerticalXL,
  },
  label: {
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  value: {
    color: tokens.colorNeutralForeground2,
  },
  skuTable: {
    marginTop: tokens.spacingVerticalL,
    // Target the table container
    "& [role='table']": {
      display: "flex",
      flexDirection: "column",
      width: "100%",
    },
    // Target the header and body rows
    "& [role='row']": {
      display: "flex",
      width: "100%",
    },
  },
  skuColumn: {
    width: "120px !important",
    minWidth: "120px",
    maxWidth: "120px",
    flexShrink: 0,
    flexGrow: 0,
  },
  itemNameColumn: {
    flex: "1 1 200px !important",
    minWidth: "200px",
    maxWidth: "none",
  },
  purchasingVolumeColumn: {
    width: "140px !important",
    minWidth: "140px",
    maxWidth: "140px",
    flexShrink: 0,
    flexGrow: 0,
  },
  amountColumn: {
    width: "80px !important",
    minWidth: "80px",
    maxWidth: "80px",
    flexShrink: 0,
    flexGrow: 0,
  },
  currentStockColumn: {
    width: "100px !important",
    minWidth: "100px",
    maxWidth: "100px",
    flexShrink: 0,
    flexGrow: 0,
  },
  npvColumn: {
    width: "120px !important",
    minWidth: "120px",
    maxWidth: "120px",
    flexShrink: 0,
    flexGrow: 0,
  },
  cfroiColumn: {
    width: "100px !important",
    minWidth: "100px",
    maxWidth: "100px",
    flexShrink: 0,
    flexGrow: 0,
  },
  tableHeaderCell: {
    fontWeight: tokens.fontWeightSemibold,
  },
  highlightedCell: {
    // backgroundColor: tokens.colorPaletteYellowBackground2,
  },
  currency: {
    textAlign: "right",
    justifyContent: "flex-end",
  },
  currencyHeader: {
    "& > div": {
      textAlign: "right",
      justifyContent: "flex-end",
    },
  },
  cfroi: {
    textAlign: "center",
  },
  clickableRow: {
    cursor: "pointer",
    "&:hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  noSalesDataRow: {
    opacity: "0.75",
  },
  noSalesDataBadge: {
    fontSize: tokens.fontSizeBase100,
  },
  itemNameCell: {
    "& > div": {
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
  },
});

interface OrderInformationProps {
  // For PurchaseApprovalPage usage
  orderData?: OrderData;
  cfroiData?: CFROIResponse;
  paymentTerms?: number;
  showSkuInteraction?: boolean;

  // For ApprovalDetailsPage usage
  approvalDetails?: ApprovalDetails;

  // Common props
  title?: string;
  showCharts?: boolean;
}

const OrderInformation: React.FC<OrderInformationProps> = ({
  orderData,
  cfroiData,
  paymentTerms,
  showSkuInteraction = false,
  approvalDetails,
  title = "Order Information",
  showCharts = true,
}) => {
  const styles = useStyles();
  const [selectedSkuData, setSelectedSkuData] = useState<{
    sku: string;
    itemName: string;
    cfroi: number;
    investment: number;
    currentStock: number;
    npv: number;
    paymentSeries: {
      b2cEntries: PaymentEntry[];
      b2bEntries: PaymentEntry[] | null;
      totalRevenue: number;
      b2cTotalRevenue: number;
      b2bTotalRevenue: number;
    };
  } | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Map different data sources to common format
  const commonOrderDetails: CommonOrderDetails | null = orderData
    ? mapOrderDataToCommon(orderData, cfroiData)
    : approvalDetails
      ? mapApprovalDetailsToCommon(approvalDetails, paymentTerms)
      : null;

  // Create CFROI response for charts
  const displayCfroiData: CFROIResponse | undefined =
    cfroiData ||
    (approvalDetails
      ? createCFROIResponseFromApproval(approvalDetails)
      : undefined);

  // Determine if SKU interaction should be enabled
  const enableSkuInteraction =
    showSkuInteraction &&
    commonOrderDetails &&
    hasSkuPaymentSeries(commonOrderDetails);

  const effectivePaymentTerms =
    paymentTerms !== undefined ? paymentTerms : (commonOrderDetails?.paymentTerms ?? 30);

  const handleSkuClick = useCallback(
    (sku: SKUItem) => {
      if (!enableSkuInteraction || !commonOrderDetails?.skuPaymentSeries) {
        return;
      }

      const enhancedSkuData = getEnhancedSkuData(
        sku,
        commonOrderDetails.skuPaymentSeries,
      );
      if (enhancedSkuData) {
        setSelectedSkuData(enhancedSkuData);
        setIsModalOpen(true);
      }
    },
    [enableSkuInteraction, commonOrderDetails?.skuPaymentSeries],
  );

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("de-DE", {
      style: "currency",
      currency: "EUR",
    }).format(amount);
  };

  if (!commonOrderDetails) {
    return null;
  }

  const skuPaymentSeries = commonOrderDetails.skuPaymentSeries;
  const hasFinancialData = !!(skuPaymentSeries && skuPaymentSeries.length > 0);
  const missingSalesDataCount = skuPaymentSeries?.filter((s) => !s.hasSalesData).length ?? 0;
  const allMissingSalesData = hasFinancialData && missingSalesDataCount === skuPaymentSeries!.length;
  const cfroiDisplay = allMissingSalesData
    ? "—"
    : `${(commonOrderDetails.portfolioCfroi * 100).toFixed(2)}%`;

  return (
    <>
      <Card className={styles.orderInfoSection}>
        <Text
          size={500}
          weight="semibold"
          style={{ marginBottom: tokens.spacingVerticalL }}
        >
          {title}
        </Text>

        <div className={styles.infoGrid}>
          <Text className={styles.label}>Purchasing entity</Text>
          <Text className={styles.value}>
            {commonOrderDetails.purchasingEntity}
          </Text>

          <Text className={styles.label}>Purchasing Manager</Text>
          <Text className={styles.value}>
            {commonOrderDetails.purchasingManager}
          </Text>

          <Text className={styles.label}>CFROI</Text>
          <Text className={styles.value}>{cfroiDisplay}</Text>
          <Text className={styles.label}>Supplier</Text>
          <Text className={styles.value}>{commonOrderDetails.supplier || "Not available"}</Text>
          <Text className={styles.label}>ERP payment terms</Text>
          <Text className={styles.value}>{commonOrderDetails.erpPaymentTerms || "Not available"}</Text>
          <Text className={styles.label}>Calculation payment terms</Text>
          <Text className={styles.value}>
            {effectivePaymentTerms} days
          </Text>
        </div>

        {/* Portfolio Cashflow Chart */}
        {showCharts && displayCfroiData && (
          <PortfolioCashflowChart
            cfroiData={displayCfroiData}
            paymentTerms={effectivePaymentTerms}
          />
        )}

        {/* SKU Table */}
        {commonOrderDetails.skus && commonOrderDetails.skus.length > 0 && (
          <div className={styles.skuTable}>
            {missingSalesDataCount > 0 && (
              <MessageBar
                intent="warning"
                style={{ marginTop: tokens.spacingVerticalM, marginBottom: tokens.spacingVerticalS }}
              >
                <MessageBarBody>
                  <Warning20Regular style={{ verticalAlign: "middle", marginRight: "6px" }} />
                  {allMissingSalesData
                    ? "No sales data available for any SKU in this order. CFROI cannot be calculated."
                    : `${missingSalesDataCount} SKU${missingSalesDataCount > 1 ? "s are" : " is"} missing sales data and ${missingSalesDataCount > 1 ? "have" : "has"} been excluded from the CFROI calculation. Results may be inaccurate.`}
                </MessageBarBody>
              </MessageBar>
            )}
            {enableSkuInteraction && (
              <Caption1
                style={{
                  marginTop: tokens.spacingVerticalM,
                  marginBottom: tokens.spacingVerticalM,
                  marginLeft: tokens.spacingHorizontalS,
                  color: tokens.colorNeutralForeground3,
                }}
              >
                Click on a SKU row with sales data to view detailed revenue analysis
              </Caption1>
            )}
            <Table noNativeElements>
              <TableHeader>
                <TableRow>
                  <TableHeaderCell
                    className={mergeClasses(
                      styles.tableHeaderCell,
                      styles.skuColumn,
                    )}
                  >
                    SKU
                  </TableHeaderCell>
                  <TableHeaderCell
                    className={mergeClasses(
                      styles.tableHeaderCell,
                      styles.itemNameColumn,
                    )}
                  >
                    Item name
                  </TableHeaderCell>
                  <TableHeaderCell
                    className={mergeClasses(
                      styles.tableHeaderCell,
                      styles.currencyHeader,
                      styles.purchasingVolumeColumn,
                    )}
                  >
                    Purchasing volume
                  </TableHeaderCell>
                  <TableHeaderCell
                    className={mergeClasses(
                      styles.tableHeaderCell,
                      styles.currencyHeader,
                      styles.amountColumn,
                    )}
                  >
                    Amount
                  </TableHeaderCell>
                  {hasFinancialData && (
                    <TableHeaderCell
                      className={mergeClasses(
                        styles.tableHeaderCell,
                        styles.currencyHeader,
                        styles.currentStockColumn,
                      )}
                    >
                      Current stock
                    </TableHeaderCell>
                  )}
                  {hasFinancialData && (
                    <TableHeaderCell
                      className={mergeClasses(
                        styles.tableHeaderCell,
                        styles.currencyHeader,
                        styles.npvColumn,
                      )}
                    >
                      NPV
                    </TableHeaderCell>
                  )}
                  {hasFinancialData && (
                    <TableHeaderCell
                      className={mergeClasses(
                        styles.tableHeaderCell,
                        styles.currencyHeader,
                        styles.cfroiColumn,
                      )}
                    >
                      CFROI
                    </TableHeaderCell>
                  )}
                  {hasFinancialData && commonOrderDetails.skus.some((sku: SKUItem) => !!sku.dailySalesRate) && (
                    <TableHeaderCell
                      className={mergeClasses(
                        styles.tableHeaderCell,
                        styles.currencyHeader,
                      )}
                    >
                      Sales Rate
                    </TableHeaderCell>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {commonOrderDetails.skus.map((sku: SKUItem, index: number) => {
                  const hasSalesData = sku.hasSalesData !== false;
                  const isClickable = enableSkuInteraction && hasSalesData;
                  return (
                  <TableRow
                    key={sku.skuId || index}
                    className={mergeClasses(
                      isClickable ? styles.clickableRow : "",
                      !hasSalesData ? styles.noSalesDataRow : "",
                    )}
                    onClick={() => isClickable && handleSkuClick(sku)}
                  >
                    <TableCell
                      className={mergeClasses(
                        styles.highlightedCell,
                        styles.skuColumn,
                      )}
                    >
                      <TableCellLayout>
                        <Text weight="semibold">{sku.skuId}</Text>
                      </TableCellLayout>
                    </TableCell>
                    <TableCell
                      className={mergeClasses(
                        styles.highlightedCell,
                        styles.itemNameColumn,
                        styles.itemNameCell,
                      )}
                    >
                      <TableCellLayout>
                        <Text weight="semibold">{sku.itemName}</Text>
                        {!hasSalesData && (
                          <Badge
                            appearance="tint"
                            color="warning"
                            className={styles.noSalesDataBadge}
                            style={{ marginLeft: "8px" }}
                          >
                            No sales data
                          </Badge>
                        )}
                      </TableCellLayout>
                    </TableCell>
                    <TableCell
                      className={mergeClasses(
                        styles.highlightedCell,
                        styles.currency,
                        styles.purchasingVolumeColumn,
                      )}
                    >
                      {formatCurrency(sku.purchasingVolume)}
                    </TableCell>
                    <TableCell
                      className={mergeClasses(
                        styles.highlightedCell,
                        styles.currency,
                        styles.amountColumn,
                      )}
                    >
                      {sku.amount}
                    </TableCell>
                    {hasFinancialData && (
                      <TableCell
                        className={mergeClasses(
                          styles.currency,
                          styles.currentStockColumn,
                        )}
                      >
                        {hasSalesData ? sku.currentStock : "—"}
                      </TableCell>
                    )}
                    {hasFinancialData && (
                      <TableCell
                        className={mergeClasses(
                          styles.highlightedCell,
                          styles.currency,
                          styles.npvColumn,
                        )}
                      >
                        {hasSalesData ? formatCurrency(sku.npv) : "—"}
                      </TableCell>
                    )}
                    {hasFinancialData && (
                      <TableCell
                        className={mergeClasses(
                          styles.cfroi,
                          styles.currency,
                          styles.cfroiColumn,
                        )}
                      >
                        {hasSalesData ? (
                          <Badge appearance="tint" color="informative">
                            {(sku.cfroi * 100).toFixed(2)}%
                          </Badge>
                        ) : "—"}
                      </TableCell>
                    )}
                    {hasFinancialData && commonOrderDetails.skus.some((s: SKUItem) => !!s.dailySalesRate) && (
                      <TableCell className={mergeClasses(styles.currency)}>
                        {hasSalesData && sku.dailySalesRate != null
                          ? `${sku.dailySalesRate.toFixed(1)} units/day`
                          : "—"}
                      </TableCell>
                    )}
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      {/* SKU Charts Modal */}
      {enableSkuInteraction && (
        <SkuChartsModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          skuData={selectedSkuData}
        />
      )}
    </>
  );
};

export default OrderInformation;
